// When a loading screen is on screen, as a state machine over the load's
// phase and the time, both given as messages (nothing here reads a clock
// or arms a timer):
//
// - a load that ends within SHOW_AFTER_MS never shows the screen: the view
//   it loads appears in its place, with nothing flashed before it;
// - a screen that is shown stays for MIN_SHOWN_MS at least, so it is never
//   a flicker either. A gate whose view has gates of its own may pass 0
//   for that minimum: it opens the moment its load is in, so the loads
//   inside start at once, and the gate inside keeps the screen up for
//   what is left of the minimum;
// - a load that failed shows the screen at once, since it then says why;
// - what was ready from the start (a page still held in memory) never
//   shows it;
// - a view that mounts still loading just as another's gate opened onto it
//   (the session, then the page; the page, then its files) takes over
//   that gate's wait. Where nothing was shown yet, the delay is not
//   started again. Where a screen was shown, it stays up as it was
//   (`covered`) while this load is younger than the delay, then becomes
//   this load's own: no gap between the two, and a view that is in at
//   once (a saved copy) never shows steps of its own.
//
// `step` answers the next state and when it wants to hear the time again
// (`wake`, epoch ms; null: not until the phase changes). Whoever runs it
// arms that one timer and sends `time` when it fires.

/** Longest a load runs before its loading screen is shown. */
export const SHOW_AFTER_MS = 180;
/** Shortest a loading screen stays once shown. */
export const MIN_SHOWN_MS = 300;

/** A load as its view tells it: under way, stopped by a failure, or done. */
export type Phase = 'loading' | 'failed' | 'ready';

export type Gate =
  /** Nothing is loading: the view is shown. */
  | { readonly at: 'open' }
  /** Loading since `since`, with nothing shown yet. */
  | { readonly at: 'pending'; readonly since: number }
  /** The loading screen is shown, since `since`. */
  | { readonly at: 'shown'; readonly since: number }
  /** Loading since `since`, under the screen the gate before left up, shown since `shown`. */
  | { readonly at: 'covered'; readonly since: number; readonly shown: number }
  /** The load is done; the loading screen stays until `until`. */
  | { readonly at: 'lingering'; readonly until: number };

export type GateMsg = { readonly t: 'phase'; readonly phase: Phase; readonly now: number } | { readonly t: 'time'; readonly now: number };

/** A state and when to send `time` next (null: no timer). */
export type GateNext = readonly [Gate, number | null];

const OPEN: Gate = Object.freeze({ at: 'open' });

const pending = (since: number): GateNext => [{ at: 'pending', since }, since + SHOW_AFTER_MS];
const shown = (since: number): GateNext => [{ at: 'shown', since }, null];

/** How lately another gate must have opened for a view mounting now to take over from it. */
export const HANDOFF_MS = 50;

/**
 * The gate of a view first seen in `phase` at `now`. `left` is the gate
 * that just opened onto it, as it was before it did (see the file comment).
 */
export function open(phase: Phase, now: number, left: Gate | null = null): GateNext {
  if (phase === 'ready') return [OPEN, null];
  const up = left?.at === 'shown' ? left.since : left?.at === 'covered' ? left.shown : left?.at === 'lingering' ? left.until - MIN_SHOWN_MS : null;
  if (phase === 'failed') return shown(up ?? now);
  if (up !== null) return [{ at: 'covered', since: now, shown: up }, now + SHOW_AFTER_MS];
  return left?.at === 'pending' ? step({ at: 'pending', since: left.since }, { t: 'time', now }) : pending(now);
}

/** Leaving a screen that has been up since `since`: at once when its minimum `min` has passed, else once it has. */
function leave(since: number, now: number, min: number): GateNext {
  const until = since + min;
  return now >= until ? [OPEN, null] : [{ at: 'lingering', until }, until];
}

/** The gate after `m`; `min` is the shortest its screen stays once shown. */
export function step(g: Gate, m: GateMsg, min: number = MIN_SHOWN_MS): GateNext {
  if (m.t === 'time') {
    if (g.at === 'pending') return m.now >= g.since + SHOW_AFTER_MS ? shown(m.now) : [g, g.since + SHOW_AFTER_MS];
    if (g.at === 'covered') return m.now >= g.since + SHOW_AFTER_MS ? shown(g.shown) : [g, g.since + SHOW_AFTER_MS];
    if (g.at === 'lingering') return m.now >= g.until ? [OPEN, null] : [g, g.until];
    return [g, null];
  }
  switch (g.at) {
    case 'open':
      return open(m.phase, m.now);
    case 'pending':
      return m.phase === 'ready' ? [OPEN, null] : m.phase === 'failed' ? shown(m.now) : [g, g.since + SHOW_AFTER_MS];
    case 'shown':
      return m.phase === 'ready' ? leave(g.since, m.now, min) : [g, null];
    case 'covered':
      return m.phase === 'ready' ? leave(g.shown, m.now, min) : m.phase === 'failed' ? shown(g.shown) : [g, g.since + SHOW_AFTER_MS];
    case 'lingering':
      // Loading again (or failed) before the screen left: it stays, as shown since it first was.
      return m.phase === 'ready' ? [g, g.until] : shown(g.until - min);
  }
}

/** What is on screen: the view, nothing yet, the loading screen the gate before left up (`cover`), or this load's own. */
export function showing(g: Gate): 'loader' | 'cover' | 'view' | 'nothing' {
  return g.at === 'open' ? 'view' : g.at === 'pending' ? 'nothing' : g.at === 'covered' ? 'cover' : 'loader';
}
